import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import styled from '@mui/material/styles/styled';

import { useForm, useFieldArray } from 'react-hook-form';
// eslint-disable-next-line import/no-unresolved
import { yupResolver } from '@hookform/resolvers/yup';
import { withStyles } from 'tss-react/mui';

import ValidationSchema from './validation-schema';
import DeleteQuestionModal from './components/delete-question-modal';
import DeleteSectionModal from './components/delete-section-modal';
import EditSectionIconModal from './components/section-icon-modal';
import AbandonChangesModal from '../../../components/abandon-changes-modal';
import DebouncedTextField from './components/debounced-text-field';
import Section from './components/section';

import PrimaryButton from '../../../components/buttons/primary-button';
import SecondaryButton from '../../../components/buttons/secondary-button';
import Colors from '../../../styles/colors';
import CombineStyles from '../../../utils/combine-styles';
import ButtonStyles from '../../../styles/buttons';
import Styles from '../styles';
import WebClient from '../../../utils/web-client';
import { APISuccess, APIError } from '../../../redux/app';
import handleItemReordering from '../../../utils/question-ordering';

const StyledPaper = styled(Paper)({
    borderRadius: 10,
    border: `1px solid ${Colors.lavenderGray}`,
    padding: 25,
    width: '100%',
});

const questionInitialValue = {
    text: '',
    type: 'radioButton',
    required: true,
    questions: [],
    options: [],
    renderLogic: {
        condition: undefined,
        value: undefined,
    },
};

const TestBuilder = ({ classes }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [loadingMessage, setLoadingMessage] = useState('Loading test data...');
    const {
        handleSubmit,
        register,
        setValue,
        getValues,
        reset,
        control,
        watch,
        clearErrors,
        formState: { errors },
    } = useForm({
        reValidateMode: 'onBlur',
        resolver: yupResolver(ValidationSchema),
        defaultValues: {},
    });

    // Watch only the sections field structure, not individual values
    const watchedSections = watch('sections');

    const { fields, append, remove, move, update } = useFieldArray({
        control,
        name: 'sections',
        keyName: 'id',
    });

    // hydrate form on page load
    useEffect(() => {
        setLoadingMessage('Loading test data...');

        WebClient.get('/test')
            .then((reponse) => {
                setLoadingMessage('Processing test structure...');

                const defaultValues = reponse.data;
                delete defaultValues.id;
                delete defaultValues.createdAt;
                delete defaultValues.userId;

                // Use setTimeout to allow the loading message to update
                setTimeout(() => {
                    reset(defaultValues);

                    // Give a brief moment for the form to render
                    setTimeout(() => {
                        setIsLoading(false);
                    }, 100);
                }, 50);
            })
            .catch((error) => {
                setIsLoading(false);
                dispatch(APIError({ text: error?.response?.data?.errors?.error[0] }));
            });
    }, [reset, dispatch]);

    useEffect(() => {
        // go through each section, looking at the questions array and the questions array of each question
        // if the question is of type instructions, we want to clear out the questions array
        // grandchild questions dont support conditional children, so we don't need to worry about them
        let requiredUpdate = false;

        if (watchedSections) {
            watchedSections.forEach((section, sectionIndex) => {
                section.questions?.forEach((question, questionIndex) => {
                    if (question.type === 'instructions' && question.questions?.length > 0) {
                        setValue(`sections[${sectionIndex}].questions[${questionIndex}].questions`, []);
                        setValue(`sections[${sectionIndex}].questions[${questionIndex}].options`, []);
                        requiredUpdate = true;
                    }
                    question.questions?.forEach((childQuestion, childQuestionIndex) => {
                        if (childQuestion.type === 'instructions' && childQuestion.questions?.length > 0) {
                            setValue(
                                `sections[${sectionIndex}].questions[${questionIndex}].questions[${childQuestionIndex}].questions`,
                                [],
                            );
                            setValue(
                                `sections[${sectionIndex}].questions[${questionIndex}].questions[${childQuestionIndex}].options`,
                                [],
                            );
                            requiredUpdate = true;
                        }
                    });
                });
            });
            if (requiredUpdate) update();
        }
    }, [watchedSections, setValue, update, isLoading]);

    const handleAddSection = () => {
        append({
            id: uuidv4(),
            name: '',
            icon: null,
            questions: [{ ...questionInitialValue, id: uuidv4() }],
        });
    };

    const handleAddQuestion = (sectionIndex, questionIndex, childQuestionIndex) => {
        if (childQuestionIndex !== undefined) {
            // if we have a child question index, we are adding a grandchild question
            const currentQuestions =
                fields[sectionIndex]?.questions?.[questionIndex]?.questions?.[childQuestionIndex]?.questions || [];
            setValue(
                `sections[${sectionIndex}].questions[${questionIndex}].questions[${childQuestionIndex}].questions`,
                [
                    ...currentQuestions,
                    {
                        ...questionInitialValue,
                        id: uuidv4(),
                    },
                ],
            );
        } else if (questionIndex !== undefined) {
            // if we have a question index, we are adding a child question
            const currentQuestions = fields[sectionIndex]?.questions?.[questionIndex]?.questions || [];
            setValue(`sections[${sectionIndex}].questions[${questionIndex}].questions`, [
                ...currentQuestions,
                {
                    ...questionInitialValue,
                    id: uuidv4(),
                },
            ]);
        } else {
            // adding a top-level question to a section
            const currentQuestions = fields[sectionIndex]?.questions || [];
            setValue(`sections[${sectionIndex}].questions`, [
                ...currentQuestions,
                {
                    ...questionInitialValue,
                    id: uuidv4(),
                },
            ]);
        }
        update();
    };
    const handleRemoveQuestion = useCallback(
        (payload) => {
            const { sectionIndex, questionIndex, childQuestionIndex, grandchildQuestionIndex } = payload;

            // Get current form values (which reflect any reordering)
            const currentValues = getValues();
            const currentSection = currentValues.sections?.[sectionIndex];

            // Ensure the section exists before proceeding
            if (!currentSection?.questions) return;

            if (grandchildQuestionIndex !== undefined) {
                // if we have a grandchild question index, we are removing a grandchild question
                const grandchildQuestions =
                    currentSection.questions?.[questionIndex]?.questions?.[childQuestionIndex]?.questions;
                if (grandchildQuestions && grandchildQuestions.length > grandchildQuestionIndex) {
                    const updatedGrandchildQuestions = [...grandchildQuestions];
                    updatedGrandchildQuestions.splice(grandchildQuestionIndex, 1);
                    setValue(
                        `sections[${sectionIndex}].questions[${questionIndex}].questions[${childQuestionIndex}].questions`,
                        updatedGrandchildQuestions,
                        { shouldDirty: true, shouldValidate: true },
                    );
                }
            } else if (childQuestionIndex !== undefined) {
                // if we have a child question index, we are removing a child question
                const childQuestions = currentSection.questions?.[questionIndex]?.questions;
                if (childQuestions && childQuestions.length > childQuestionIndex) {
                    const updatedChildQuestions = [...childQuestions];
                    updatedChildQuestions.splice(childQuestionIndex, 1);
                    setValue(`sections[${sectionIndex}].questions[${questionIndex}].questions`, updatedChildQuestions, {
                        shouldDirty: true,
                        shouldValidate: true,
                    });
                }
            } else {
                // removing a top-level question
                const { questions } = currentSection;
                if (questions && questions.length > questionIndex) {
                    const updatedQuestions = [...questions];
                    updatedQuestions.splice(questionIndex, 1);
                    setValue(`sections[${sectionIndex}].questions`, updatedQuestions, {
                        shouldDirty: true,
                        shouldValidate: true,
                    });
                }
            }
        },
        [getValues, setValue],
    );

    const handleRemoveSection = (sectionIndex) => {
        remove(sectionIndex);
    };

    const handleQuestionOrder = (sectionIndex, questionIndex, destinationQuestionIndex) => {
        handleItemReordering(getValues, setValue, clearErrors, {
            sectionIndex,
            sourceIndex: questionIndex,
            destinationIndex: destinationQuestionIndex,
        });
    };

    const handleChildQuestionOrder = (sectionIndex, questionIndex, childQuestionIndex, destinationQuestionIndex) => {
        handleItemReordering(getValues, setValue, clearErrors, {
            sectionIndex,
            questionIndex,
            sourceIndex: childQuestionIndex,
            destinationIndex: destinationQuestionIndex,
        });
    };

    const handleGrandchildQuestionOrder = (
        sectionIndex,
        questionIndex,
        childQuestionIndex,
        grandchildQuestionIndex,
        destinationQuestionIndex,
    ) => {
        handleItemReordering(getValues, setValue, clearErrors, {
            sectionIndex,
            questionIndex,
            childQuestionIndex,
            sourceIndex: grandchildQuestionIndex,
            destinationIndex: destinationQuestionIndex,
        });
    };

    const onSubmit = (data) => {
        const payload = data;
        // remove the organizationId from the payload, since it's not allowed to be set by the user
        delete payload.organizationId;
        WebClient.post('/tests', payload)
            .then(() => {
                dispatch(APISuccess({ text: 'Test Successfully saved' }));
                navigate('/dashboard');
            })
            .catch((error) => {
                dispatch(APIError({ text: error?.response?.data?.errors?.error[0] }));
            });
    };

    const [editSectionIconModalOpen, setEditSectionIconModalOpen] = useState(false);
    const [editingIconFieldName, setEditingIconFieldName] = useState('');
    const [editingIconValue, setEditingIconValue] = useState(null);
    const handleEditSectionIcon = (fieldName, sectionIndex) => {
        setEditSectionIconModalOpen(true);
        setEditingIconFieldName(fieldName);
        const currentValues = getValues();
        setEditingIconValue(currentValues.sections[sectionIndex]?.icon);
    };

    // Delete Question Modal
    const [deleteQuestionModalOpen, setDeleteQuestionModalOpen] = useState(false);
    const [handleDeleteQuestionPayload, setHandleDeleteQuestionPayload] = useState(null);
    const [handleDeleteSectionPayload, setHandleDeleteSectionPayload] = useState(null);

    const handleDeleteQuestionModal = (payload) => {
        setDeleteQuestionModalOpen(true);
        setHandleDeleteQuestionPayload(payload);
    };

    // Delete Section Modal
    const [deleteSectionModalOpen, setDeleteSectionModalOpen] = useState(false);
    const handleDeleteSectionModal = (payload) => {
        setDeleteSectionModalOpen(true);
        setHandleDeleteSectionPayload(payload);
    };

    // confirm abandon form modal
    const [abandonFormModalOpen, setAbandonFormModalOpen] = useState(false);

    // Loading screen component
    if (isLoading) {
        return (
            <Grid item container className={classes.dashboardContainer}>
                <Grid item xs={12}>
                    <Typography variant="h1">Test Builder</Typography>
                    <Typography variant="body1">
                        Add, edit, and delete questions for your report. Changes made will not impact testers who have
                        already submitted the report previously.
                    </Typography>
                </Grid>

                <Grid item xs={12} pt={5}>
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        minHeight="60vh"
                        gap={3}
                    >
                        <CircularProgress size={60} thickness={4} />
                        <Typography variant="body2" color="textSecondary">
                            {loadingMessage}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid item container className={classes.dashboardContainer}>
            <EditSectionIconModal
                open={editSectionIconModalOpen}
                // we need to update so field values are updated
                handleClose={() => {
                    setEditSectionIconModalOpen(false);
                    update();
                }}
                fieldName={editingIconFieldName}
                setValue={setValue}
                defaultValue={editingIconValue}
            />
            <DeleteQuestionModal
                open={deleteQuestionModalOpen}
                handleClose={() => setDeleteQuestionModalOpen(false)}
                handleDeletePayload={handleDeleteQuestionPayload}
                handleDelete={handleRemoveQuestion}
            />
            <DeleteSectionModal
                open={deleteSectionModalOpen}
                handleClose={() => setDeleteSectionModalOpen(false)}
                handleDeletePayload={handleDeleteSectionPayload}
                handleDelete={handleRemoveSection}
            />
            <AbandonChangesModal
                open={abandonFormModalOpen}
                handleClose={() => setAbandonFormModalOpen(false)}
                handleConfirm={() => navigate(-1)}
            />

            <Grid item xs={12}>
                <Typography variant="h1">Test Builder</Typography>
                <Typography variant="body1">
                    Add, edit, and delete questions for your report. Changes made will not impact testers who have
                    already submitted the report previously.
                </Typography>
            </Grid>

            <Grid item xs={12} pt={5}>
                <StyledPaper>
                    <div style={{ marginBottom: 20 }}>
                        <DebouncedTextField
                            errors={errors}
                            name="name"
                            label="Test Name"
                            required
                            control={control}
                            style={{ width: 280 }}
                        />
                    </div>
                    <DebouncedTextField
                        errors={errors}
                        name="instructions"
                        label="Test Instructions"
                        required
                        control={control}
                        multiline
                        minRows={4}
                    />
                </StyledPaper>
            </Grid>

            {/* Sections List */}
            <Grid item xs={12} pt={5}>
                {fields.length > 0 ? (
                    fields.map((section, sectionIndex) => (
                        <Section
                            key={section.id}
                            item={section}
                            sectionIndex={sectionIndex}
                            totalSections={fields.length}
                            control={control}
                            errors={errors}
                            register={register}
                            setValue={setValue}
                            getValues={getValues}
                            handleAddQuestion={handleAddQuestion}
                            handleDeleteQuestionModal={handleDeleteQuestionModal}
                            handleDeleteSectionModal={handleDeleteSectionModal}
                            handleEditSectionIcon={handleEditSectionIcon}
                            handleQuestionOrder={handleQuestionOrder}
                            handleChildQuestionOrder={handleChildQuestionOrder}
                            handleGrandchildQuestionOrder={handleGrandchildQuestionOrder}
                            move={move}
                        />
                    ))
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Typography variant="h3" color="textSecondary">
                            No sections yet. Click &quot;Add Section&quot; to get started.
                        </Typography>
                    </div>
                )}
            </Grid>
            <Grid item container xs={12} pt={5}>
                <Grid item xs={12}>
                    <SecondaryButton className={classes.secondaryButton} onClick={handleAddSection}>
                        <AddCircleOutlineIcon style={{ marginRight: 5, fontSize: 17 }} />
                        Add Section
                    </SecondaryButton>
                </Grid>
                {/* Display form errors if any exist */}
                <Grid item xs={12} pt={5} style={{ textAlign: 'right' }}>
                    {errors.sections && (
                        <Typography variant="body1" color="error">
                            There are errors in your form. Please correct them before saving.
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={12} style={{ textAlign: 'right' }} pt={4}>
                    <SecondaryButton
                        className={classes.secondaryButton}
                        onClick={() => setAbandonFormModalOpen(true)}
                        sx={{ mr: 3 }}
                    >
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton
                        onClick={handleSubmit(onSubmit)}
                        className={classes.primaryButton}
                        classes={{
                            label: classes.buttonLabel,
                        }}
                        TouchRippleProps={{
                            classes: {
                                childPulsate: classes.primaryButtonRippleChildPulsate,
                                ripplePulsate: classes.buttonRipplePulsate,
                            },
                        }}
                    >
                        Finish and Save
                    </PrimaryButton>
                </Grid>
            </Grid>
            <Grid item container xs={12} pt={5}>
                <Grid item xs={12} style={{ textAlign: 'center' }}>
                    <Typography variant="body2">
                        No portion of this Tester Application may be reproduced, distributed, or sold in any form.
                    </Typography>
                </Grid>
            </Grid>
        </Grid>
    );
};

TestBuilder.propTypes = {
    classes: PropTypes.object.isRequired,
};

const combinedStyles = CombineStyles(ButtonStyles, Styles);
export default withStyles(TestBuilder, combinedStyles);
